import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { createAlumni, findAlumniDuplicates, findStudentCandidates, updateAlumni } from '../../api/alumni';
import { Alumni, AlumniDuplicate, AlumniFields, AlumniLookups, AlumniStatus, StudentCandidate } from '../../types/alumni';
import { ALUMNI_STATUS_LABELS, DUPLICATE_REASON_LABELS, toDateInput } from './alumniLabels';

type FormState = Record<keyof AlumniFields, string>;

const EMPTY: FormState = {
  fullName: '',
  phone: '',
  email: '',
  employer: '',
  graduationYear: '',
  countryOfWorkId: '',
  originalStudentNumber: '',
  university: '',
  course: '',
  intake: '',
  dateOfBirth: '',
  jobTitle: '',
  specialization: '',
  previousEmployer: '',
  employmentHistory: '',
  currentCity: '',
  linkedinUrl: '',
  alternativePhone: '',
  whatsappNumber: '',
  personalEmail: '',
  workEmail: '',
  status: '',
  furtherQualification: '',
  notes: '',
  lastVerifiedAt: '',
  nextFollowUpAt: '',
};

const DATE_FIELDS: Array<keyof AlumniFields> = ['dateOfBirth', 'lastVerifiedAt', 'nextFollowUpAt'];

export const fromAlumni = (a: Partial<AlumniFields>): FormState => {
  const state = { ...EMPTY };
  for (const key of Object.keys(EMPTY) as Array<keyof AlumniFields>) {
    const value = a[key];
    if (value == null) continue;
    state[key] = DATE_FIELDS.includes(key) ? toDateInput(String(value)) : String(value);
  }
  return state;
};

/** The payload for the API: blank optional fields become null (cleared). */
export const toPayload = (form: FormState): Partial<AlumniFields> => {
  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(form)) {
    const v = raw.trim();
    if (key === 'graduationYear' || key === 'countryOfWorkId') out[key] = v ? Number(v) : null;
    else out[key] = v || null;
  }
  return out as Partial<AlumniFields>;
};

const PHONE = /^\+?[\d\s()-]{7,20}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Client-side checks mirroring the server's (which has the last word). */
export const alumniFormProblems = (form: FormState) => {
  const p: Partial<Record<keyof AlumniFields, string>> = {};
  if (form.fullName.trim().length < 2) p.fullName = 'Full name is required';
  if (!form.phone.trim()) p.phone = 'Contact number is required';
  else if (!PHONE.test(form.phone.trim())) p.phone = 'Enter a valid phone number';
  for (const key of ['alternativePhone', 'whatsappNumber'] as const) {
    if (form[key].trim() && !PHONE.test(form[key].trim())) p[key] = 'Enter a valid phone number';
  }
  for (const key of ['email', 'personalEmail', 'workEmail'] as const) {
    if (form[key].trim() && !EMAIL.test(form[key].trim())) p[key] = 'Enter a valid email address';
  }
  const year = form.graduationYear.trim();
  if (year && (!/^\d{4}$/.test(year) || Number(year) < 1950 || Number(year) > new Date().getFullYear() + 1)) {
    p.graduationYear = `Enter a year between 1950 and ${new Date().getFullYear() + 1}`;
  }
  if (form.linkedinUrl.trim() && !/^https?:\/\/([a-z0-9-]+\.)*linkedin\.com\//i.test(form.linkedinUrl.trim())) {
    p.linkedinUrl = 'Enter a LinkedIn profile URL (https://www.linkedin.com/in/…)';
  }
  const today = new Date().toISOString().slice(0, 10);
  if (form.dateOfBirth && form.dateOfBirth > today) p.dateOfBirth = "Can't be in the future";
  if (form.lastVerifiedAt && form.lastVerifiedAt > today) p.lastVerifiedAt = "Can't be in the future";
  return p;
};

const errorMessage = (err: unknown) =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong.') : 'Something went wrong.';

const DuplicateList = ({ duplicates }: { duplicates: AlumniDuplicate[] }) => (
  <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.25}>
    {duplicates.map((d) => (
      <li key={d.id}>
        <Link component={RouterLink} to={`/alumni/${d.id}`} target="_blank" rel="noopener">
          {d.alumniNumber} — {d.fullName}
        </Link>{' '}
        ({d.reasons.map((r) => DUPLICATE_REASON_LABELS[r] ?? r).join(', ')}
        {d.branch ? ` · ${d.branch.name}` : ''}
        {d.archived ? ' · archived' : ''})
      </li>
    ))}
  </Stack>
);

/** Add Alumni / Edit alumni (Phase 105). Adding can link an existing
 * student (search by name, Student ID, email or phone) — their details are
 * prefilled, and a student who already has a record is pointed to it
 * instead. Possible duplicates (same email, contact number or Student ID)
 * are shown while typing; saving one anyway needs a second, explicit
 * "Save anyway". */
export const AlumniFormDialog = ({
  open,
  onClose,
  onSaved,
  alumni,
  lookups,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (alumni: Alumni) => void;
  /** Editing this record; omitted = adding. */
  alumni?: Alumni | null;
  lookups: AlumniLookups | undefined;
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const editing = !!alumni;
  const [form, setForm] = useState<FormState>(alumni ? fromAlumni(alumni) : EMPTY);
  const [student, setStudent] = useState<StudentCandidate | null>(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [branchId, setBranchId] = useState<number | ''>('');
  const [touched, setTouched] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState<{ duplicates?: AlumniDuplicate[]; existing?: { id: number; alumniNumber: string } } | null>(null);

  useEffect(() => {
    if (open) {
      setForm(alumni ? fromAlumni(alumni) : EMPTY);
      setStudent(null);
      setStudentSearch('');
      setBranchId('');
      setTouched(false);
      setServerErrors({});
      setConflict(null);
    }
  }, [open, alumni]);

  const set = (key: keyof AlumniFields) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setServerErrors((s) => ({ ...s, [key]: '' }));
    setConflict(null);
  };

  const candidates = useQuery({
    queryKey: ['alumni-student-candidates', studentSearch],
    queryFn: () => findStudentCandidates(studentSearch),
    enabled: open && studentSearch.trim().length >= 2 && (!editing || !alumni?.studentId),
  });

  // Live duplicate warning (debounced on the identifying fields).
  const probe = useMemo(
    () => ({
      email: form.email.trim() && EMAIL.test(form.email.trim()) ? form.email.trim() : undefined,
      phone: form.phone.replace(/\D/g, '').length >= 7 ? form.phone.trim() : undefined,
      studentNumber: form.originalStudentNumber.trim() || student?.studentNumber || undefined,
      excludeId: alumni?.id,
    }),
    [form.email, form.phone, form.originalStudentNumber, student, alumni],
  );
  const [debouncedProbe, setDebouncedProbe] = useState(probe);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedProbe(probe), 400);
    return () => clearTimeout(t);
  }, [probe]);
  const duplicates = useQuery({
    queryKey: ['alumni-duplicates', debouncedProbe],
    queryFn: () => findAlumniDuplicates(debouncedProbe),
    enabled: open && !!(debouncedProbe.email || debouncedProbe.phone || debouncedProbe.studentNumber),
  });
  const liveDuplicates = (duplicates.data ?? []).filter((d) => !(student && d.reasons.length === 1 && d.reasons[0] === 'student'));

  const pickStudent = (s: StudentCandidate | null) => {
    setStudent(s);
    setConflict(null);
    if (s && !s.alumni) {
      setForm((f) => ({
        ...f,
        fullName: f.fullName || `${s.firstName} ${s.lastName}`.trim(),
        phone: f.phone || s.phone,
        email: f.email || (s.email ?? ''),
        originalStudentNumber: s.studentNumber,
      }));
    }
  };

  const problems = alumniFormProblems(form);
  const valid = Object.keys(problems).length === 0;
  const fieldError = (key: keyof AlumniFields) => (touched && problems[key]) || serverErrors[key] || '';

  const save = useMutation({
    mutationFn: (allowDuplicate: boolean) => {
      const payload = toPayload(form);
      if (editing) return updateAlumni(alumni!.id, { ...payload, ...(student ? { studentId: student.id } : {}) });
      return createAlumni({
        ...(payload as AlumniFields),
        ...(student ? { studentId: student.id } : {}),
        ...(branchId ? { branchId } : {}),
        ...(allowDuplicate ? { allowDuplicate: true } : {}),
      });
    },
    onSuccess: (saved) => {
      onSaved(saved);
      onClose();
    },
    onError: (err) => {
      if (isAxiosError(err)) {
        const body = err.response?.data;
        if (err.response?.status === 409 && body?.errors?.code === 'POSSIBLE_DUPLICATE') {
          setConflict({ duplicates: body.errors.duplicates });
          return;
        }
        if (err.response?.status === 409 && body?.errors?.code === 'STUDENT_ALREADY_ALUMNI') {
          setConflict({ existing: { id: body.errors.alumniId, alumniNumber: body.errors.alumniNumber } });
          return;
        }
        if (err.response?.status === 422 && body?.errors) {
          setServerErrors(Object.fromEntries(Object.entries(body.errors as Record<string, string[]>).map(([k, v]) => [k, v.join(', ')])));
        }
      }
    },
  });

  const submit = (allowDuplicate = false) => {
    setTouched(true);
    if (!valid) return;
    save.mutate(allowDuplicate);
  };

  const text = (key: keyof AlumniFields, label: string, props: Record<string, unknown> = {}) => (
    <TextField
      label={label}
      fullWidth
      size="small"
      value={form[key]}
      onChange={set(key)}
      error={!!fieldError(key)}
      helperText={fieldError(key) || (props.helperText as string | undefined)}
      {...props}
    />
  );
  const date = (key: keyof AlumniFields, label: string) =>
    text(key, label, { type: 'date', InputLabelProps: { shrink: true } });

  const section = (title: string) => (
    <Grid item xs={12}>
      <Divider textAlign="left" sx={{ mt: 1 }}>
        <Typography variant="overline" color="text.secondary">
          {title}
        </Typography>
      </Divider>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={save.isPending ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen} aria-labelledby="alumni-form-title">
      <DialogTitle id="alumni-form-title">{editing ? `Edit ${alumni!.alumniNumber}` : 'Add Alumni'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {(!editing || !alumni?.studentId) && (
            <Grid item xs={12}>
              <Autocomplete
                options={candidates.data ?? []}
                value={student}
                onChange={(_e, s) => pickStudent(s)}
                inputValue={studentSearch}
                onInputChange={(_e, v) => setStudentSearch(v)}
                filterOptions={(x) => x}
                getOptionLabel={(s) => `${s.studentNumber} — ${s.firstName} ${s.lastName}`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                loading={candidates.isFetching}
                noOptionsText={studentSearch.trim().length < 2 ? 'Type a name, Student ID, email or phone' : 'No matching students'}
                renderOption={(props, s) => (
                  <li {...props} key={s.id}>
                    <Box>
                      <Typography variant="body2">
                        {s.studentNumber} — {s.firstName} {s.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[s.email, s.phone].filter(Boolean).join(' · ')}
                        {s.alumni ? ` · already alumni ${s.alumni.alumniNumber}` : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Link an existing student (optional)"
                    helperText="Leave empty for a former student who isn't in the system."
                  />
                )}
              />
              {student?.alumni && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {student.firstName} already has alumni record{' '}
                  <Link component={RouterLink} to={`/alumni/${student.alumni.id}`}>
                    {student.alumni.alumniNumber}
                  </Link>{' '}
                  — open and update it instead of adding another.
                </Alert>
              )}
            </Grid>
          )}
          {editing && alumni?.student && (
            <Grid item xs={12}>
              <Alert severity="info">
                Linked to student{' '}
                <Link component={RouterLink} to={`/students/${alumni.student.id}`}>
                  {alumni.student.studentNumber} — {alumni.student.firstName} {alumni.student.lastName}
                </Link>
              </Alert>
            </Grid>
          )}

          {section('Core details')}
          <Grid item xs={12} sm={6}>{text('fullName', 'Full name', { required: true })}</Grid>
          <Grid item xs={12} sm={6}>{text('phone', 'Contact number', { required: true, inputProps: { inputMode: 'tel' } })}</Grid>
          <Grid item xs={12} sm={6}>{text('email', 'Email address', { type: 'email' })}</Grid>
          <Grid item xs={12} sm={6}>{text('employer', 'Place of work / employer')}</Grid>
          <Grid item xs={6} sm={3}>{text('graduationYear', 'Graduation year', { inputProps: { inputMode: 'numeric', maxLength: 4 } })}</Grid>
          <Grid item xs={6} sm={3}>
            {text('status', 'Alumni status', {
              select: true,
              children: [
                <MenuItem key="" value="">
                  —
                </MenuItem>,
                ...(Object.keys(ALUMNI_STATUS_LABELS) as AlumniStatus[]).map((s) => (
                  <MenuItem key={s} value={s}>
                    {ALUMNI_STATUS_LABELS[s]}
                  </MenuItem>
                )),
              ],
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {text('countryOfWorkId', 'Country of work', {
              select: true,
              SelectProps: { MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } },
              children: [
                <MenuItem key="" value="">
                  —
                </MenuItem>,
                ...(lookups?.countries ?? []).map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                )),
              ],
            })}
          </Grid>

          {(liveDuplicates.length > 0 || conflict?.duplicates) && (
            <Grid item xs={12}>
              <Alert severity="warning" data-testid="alumni-duplicate-warning">
                <Typography variant="body2" fontWeight={600}>
                  Possible duplicate{(conflict?.duplicates ?? liveDuplicates).length > 1 ? 's' : ''} — check before saving:
                </Typography>
                <DuplicateList duplicates={conflict?.duplicates ?? liveDuplicates} />
              </Alert>
            </Grid>
          )}

          {section('Study history')}
          <Grid item xs={12} sm={4}>{text('originalStudentNumber', 'Original Student ID', { disabled: !!student || !!alumni?.studentId })}</Grid>
          <Grid item xs={12} sm={8}>{text('university', 'University')}</Grid>
          <Grid item xs={12} sm={8}>{text('course', 'Course / programme')}</Grid>
          <Grid item xs={12} sm={4}>{text('intake', 'Intake')}</Grid>
          <Grid item xs={12}>{text('furtherQualification', 'Further qualification / postgraduate studies', { multiline: true, minRows: 2 })}</Grid>

          {section('Employment')}
          <Grid item xs={12} sm={6}>{text('jobTitle', 'Current job title / designation')}</Grid>
          <Grid item xs={12} sm={6}>{text('specialization', 'Professional specialization')}</Grid>
          <Grid item xs={12} sm={6}>{text('previousEmployer', 'Previous employer')}</Grid>
          <Grid item xs={12} sm={6}>{text('currentCity', 'Current city')}</Grid>
          <Grid item xs={12}>{text('employmentHistory', 'Employment history', { multiline: true, minRows: 2 })}</Grid>

          {section('Other contact details')}
          <Grid item xs={12} sm={6}>{text('whatsappNumber', 'WhatsApp number', { inputProps: { inputMode: 'tel' } })}</Grid>
          <Grid item xs={12} sm={6}>{text('alternativePhone', 'Alternative contact number', { inputProps: { inputMode: 'tel' } })}</Grid>
          <Grid item xs={12} sm={6}>{text('personalEmail', 'Personal email', { type: 'email' })}</Grid>
          <Grid item xs={12} sm={6}>{text('workEmail', 'Work email', { type: 'email' })}</Grid>
          <Grid item xs={12} sm={8}>{text('linkedinUrl', 'LinkedIn profile URL')}</Grid>
          <Grid item xs={12} sm={4}>{date('dateOfBirth', 'Date of birth')}</Grid>

          {section('Follow-up')}
          <Grid item xs={12} sm={6}>{date('lastVerifiedAt', 'Details last verified')}</Grid>
          <Grid item xs={12} sm={6}>{date('nextFollowUpAt', 'Next follow-up')}</Grid>
          <Grid item xs={12}>{text('notes', 'Notes and follow-up remarks', { multiline: true, minRows: 3 })}</Grid>

          {!editing && lookups?.canChooseBranch && (
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Branch"
                value={branchId}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                onChange={(e) => setBranchId(e.target.value === '' ? '' : Number(e.target.value))}
                helperText={student ? "Defaults to the student's branch." : 'Defaults to your own branch.'}
              >
                <MenuItem value="">Default</MenuItem>
                {lookups.branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>

        {conflict?.existing && (
          <Alert severity="error" sx={{ mt: 2 }}>
            This student already has alumni record{' '}
            <Link component={RouterLink} to={`/alumni/${conflict.existing.id}`}>
              {conflict.existing.alumniNumber}
            </Link>
            . Open it instead of adding a duplicate.
          </Alert>
        )}
        {save.isError && !conflict && Object.keys(serverErrors).length === 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage(save.error)}
          </Alert>
        )}
        {touched && !valid && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Fix the highlighted fields to save.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
        {conflict?.duplicates ? (
          <Button variant="contained" color="warning" disabled={save.isPending} onClick={() => submit(true)}>
            {save.isPending ? 'Saving…' : 'Save anyway'}
          </Button>
        ) : (
          <Button variant="contained" disabled={save.isPending || !!student?.alumni} onClick={() => submit(false)}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add alumni'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
